import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Input,
  InputAdornment,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePopupContext } from '../../PopupContext';
import Context from '../../context';
import { useGetToken } from '../../hooks/useGetToken';
import { fbDeleteForm, fbDuplicateForm, fbExtendForm, fbGetExport, fbGetForm, fbGetSections, fbPostImport, fbPutForm, fbReleaseForm, FileType } from '../../services/service';
import { FbeForm, FbeFrontline, getLanguageDisplayName, } from '../../services/fbeTypes';
import { computeFormTranslationCompletion } from '../../services/translationCompletion';
import { AxiosResponse } from 'axios';

interface ContextMenuProps {
  pk: string;
  sk: string;
  title: string;
  accessToken: string;
  hasExtension: boolean;
  anchorEl: null | HTMLElement;
  open: boolean;
  isLocalAdmin: boolean;
  isGlobalAdmin: boolean;
  active: boolean;
  languages: string[];
  onClose: () => void;
  onAction: (action: string) => void;
  frontline?: FbeFrontline;
}

export const fbImportSuccessMessage = 'Imported the data!';
const fbExportSuccessMessage = 'Exported the data!';
export const fbImportFailureMessage = 'Unable to import the data!';
const fbExportFailureMessage = 'Unable to export the data!';

const getFileNameFromContentDisposition = (headerValue?: string): string | undefined => {
  if (!headerValue) {
    return undefined;
  }

  // RFC 5987 filename*=UTF-8''...
  const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  // filename="..." or filename=...
  const fileNameMatch = headerValue.match(/filename=(?:"([^"]+)"|([^;]+))/i);
  if (fileNameMatch) {
    return (fileNameMatch[1] || fileNameMatch[2] || '').trim();
  }

  return undefined;
};

const sanitizeDownloadFileName = (fileName: string): string => {
  return fileName
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .replace(/^\.+/, '')
    .trim();
};

const FormCardMenuComponent: React.FC<ContextMenuProps> = ({
  pk,
  sk,
  title,
  hasExtension,
  anchorEl,
  open,
  isLocalAdmin,
  isGlobalAdmin,
  active,
  onClose,
  languages,
  frontline,
  // accessToken prop kept for backward compat but we always use getTokenFunction for fresh tokens
}) => {
  const navigate = useNavigate();

  const deleteWarning = `Form deletion can not be undone. Do you want to delete the '${title}' form ? `;
  const deleteWarningWithExtension = `Form deletion can not be undone. This form has extensions. Do you want to delete the '${title}' form ?`;

  const isGlobalForm = pk === 'form_global';

  const showAllMenuItems =
    (isLocalAdmin && !isGlobalForm) || (isGlobalAdmin && isGlobalForm);
  const makeDisabled = !showAllMenuItems;

  const { updateIsLoading, setShowError, setShowSuccess, setMessage: setStatusMessage } = useContext(Context);

  const {
    setTrigger,
    setTitle,
    setMessage,
    setAction,
    setExecuteMethod,
    setMethod,
    setPk,
    setSk,
    executeMethod,
  } = usePopupContext();

  useEffect(() => {
    const execute = async () => {
      if (executeMethod === `${pk}-${sk}-handleDeleteForm`) {
        await handleDeleteForm();
        setExecuteMethod('');
      }
    };

    execute();
  }, [executeMethod]);

  const [getTokenFunction] = useGetToken();

  const handleReleaseForm = async (pk: string, sk: string) => {
    const accessToken = await getTokenFunction();

    updateIsLoading(true);

    await fbReleaseForm({ pk, sk } as FbeForm, accessToken)
      .then(() => {
        window.location.reload();
      })
      .catch((reason) => {
        updateIsLoading(false);
        setStatusMessage(reason);
        setShowError(true);
      })
      .finally(() => {
        updateIsLoading(false);
      });
  };

  const handleDeleteForm = async () => {
    const accessToken = await getTokenFunction();
    updateIsLoading(true);
    await fbDeleteForm({ pk, sk } as FbeForm, accessToken)
      .then(() => {
        window.location.reload();
        //      updateIsLoading(false);
      })
      .catch((reason) => {
        updateIsLoading(false);
        setStatusMessage(reason);
        setShowError(true);
      });
  };

  const handleActivationForm = async (pk: string, sk: string, activate: boolean) => {
    try{
      const accessToken = await getTokenFunction();

      updateIsLoading(true);
      const form = await fbGetForm( pk, sk , accessToken)
      if(form){
        await fbPutForm({...form, active: activate}, accessToken);
      }
      window.location.reload();
    } catch(error){
      updateIsLoading(false);
      setStatusMessage(`${error}`);
      setShowError(true);
    }
  };

  const fbDuplicateErrorMessage = 'Unable to duplicate form!';
  const fbDuplicateSuccessMessage = 'Duplicated form!';

  const handleDuplicateForm = async (pk: string, sk: string) => {
    try{
      const accessToken = await getTokenFunction();

      updateIsLoading(true);

      const referenceId = await fbDuplicateForm( pk, sk , accessToken);
      if(!referenceId){
        updateIsLoading(false);
        setStatusMessage(fbDuplicateErrorMessage);
        setShowError(true);
        return;
      } else {
        setStatusMessage(fbDuplicateSuccessMessage);
        setShowSuccess(true);
      }

      const parts = referenceId.referenceId.split('#');
      if(parts.length === 2){
        const duplicateForm = await fbGetForm(parts[0], parts[1], accessToken);
        updateIsLoading(false);
        navigate('/edit', { state: { formData: duplicateForm } });
      } else {
        updateIsLoading(false);
        setStatusMessage(fbDuplicateErrorMessage);
        setShowError(true);
      }
    } catch(error){
      updateIsLoading(false);
      setStatusMessage(`${error}`);
      setShowError(true);
    }
  };

  const fbExtendErrorMessage = 'Unable to extend global form!';
  const fbExtendSuccessMessage = 'Extended global form!';

  const handleExtendForm = async (pk: string, sk: string) => {
    try{
      const accessToken = await getTokenFunction();

      updateIsLoading(true);

      const referenceId = await fbExtendForm( pk, sk , accessToken);
      if(!referenceId){
        updateIsLoading(false);
        setStatusMessage(fbExtendErrorMessage);
        setShowError(true);
        return;
      } else {
        setStatusMessage(fbExtendSuccessMessage);
        setShowSuccess(true);
      }

      const parts = referenceId.referenceId.split('#');
      if(parts.length === 2){
        const extendForm = await fbGetForm(parts[0], parts[1], accessToken);
        updateIsLoading(false);
        navigate('/edit', { state: { formData: extendForm } });
      } else {
        updateIsLoading(false);
        setStatusMessage(fbExtendErrorMessage);
        setShowError(true);
      }
    } catch(error){
      updateIsLoading(false);
      setStatusMessage(`${error}`);
      setShowError(true);
    }
  };

  const [submenuAnchorElImport, setSubmenuAnchorElImport] = useState<null | HTMLElement>(null);
  const [submenuAnchorElTranslations, setSubmenuAnchorElTranslations] = useState<null | HTMLElement>(null);
  const [translationsDialogOpen, setTranslationsDialogOpen] = useState(false);
  const [translationsDialogSearch, setTranslationsDialogSearch] = useState('');
  const [submenuOpenLeft, setSubmenuOpenLeft] = useState(false);
  // null = loading/not-yet-fetched, false = fetch failed, Record = loaded
  const [translationCompletion, setTranslationCompletion] = useState<Record<string, number> | null | false>(null);
  const sortedLanguages = useMemo(
    () => [...languages].sort((a, b) =>
      getLanguageDisplayName(a, frontline?.settings.languageDisplayNames || {})
        .localeCompare(getLanguageDisplayName(b, frontline?.settings.languageDisplayNames || {}))
    ),
    [frontline?.settings.languageDisplayNames, languages],
  );

  // Clear translations submenu whenever the parent menu closes, to prevent
  // a stale anchor pointing at a detached DOM element (which causes the
  // language list to appear at the wrong position on screen).
  useEffect(() => {
    if (!open) {
      setSubmenuAnchorElTranslations(null);
      // Reset failed state so re-opening retries the fetch
      if (translationCompletion === false) {
        setTranslationCompletion(null);
      }
    }
  }, [open]);
  const fetchTranslationCompletion = async () => {
    if (translationCompletion !== null) return; // already fetched or failed — don't retry
    try {
      const token = await getTokenFunction();
      const [form, sections] = await Promise.all([
        fbGetForm(pk, sk, token),
        fbGetSections(pk, sk, token),
      ]);
      if (form) {
        const formWithLanguages = {
          ...form,
          languages: (form.languages && form.languages.length > 0) ? form.languages : languages,
        };
        setTranslationCompletion(computeFormTranslationCompletion(formWithLanguages, sections || []));
      } else {
        setTranslationCompletion(false); // form not found
      }
    } catch {
      setTranslationCompletion(false); // fetch failed — stop spinner, show dash
    }
  };

  const handleTranslationsMenuClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    if (isGlobalAdmin && isGlobalForm) {
      // For global admins on global forms, show full dialog instead of cramped submenu.
      // Close the parent context menu first so it doesn't sit behind the dialog.
      onClose();
      setTranslationsDialogOpen(true);
      setTranslationsDialogSearch('');
      fetchTranslationCompletion();
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setSubmenuOpenLeft(rect.right + 220 > window.innerWidth);
      setSubmenuAnchorElTranslations(event.currentTarget);
      fetchTranslationCompletion();
    }
  };

  const handleTranslationsMenuClose = () => {
    setSubmenuAnchorElTranslations(null);
  };

  const handleSubMenuImportClose = () => {
    setSubmenuAnchorElImport(null);
  };

  const handleExportForm = async () => {
    closeSubMenus();
    try {
      const accessToken = await getTokenFunction();
      updateIsLoading(true);
      const response = await fbGetExport(FileType.FORM, `${pk}%23${sk}%23`, accessToken);
      if (response && response.data) {
        const blob = new Blob([JSON.stringify(response.data)], { type: response.headers['content-type'] as string | undefined });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
        const defaultFileName = `form_${pk}_${sk}.json`;
        const headerFileName = getFileNameFromContentDisposition(contentDisposition);
        const fileName = sanitizeDownloadFileName(headerFileName || defaultFileName) || defaultFileName;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);
        setStatusMessage(fbExportSuccessMessage);
        setShowSuccess(true);
      } else {
        setStatusMessage(fbExportFailureMessage);
        setShowError(true);
      }
      updateIsLoading(false);
    } catch (error) {
      updateIsLoading(false);
      setStatusMessage(`${error}`);
      setShowError(true);
    }
  };

  const [fileType, setFileType] = useState<FileType>(FileType.FORM);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');

  const handleImportMenuClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setSubmenuAnchorElImport(event.currentTarget);
  };

  const handleImportOption = (fileType: FileType, language?: string) => {
    const fileInput = document.getElementById(`${pk}_${sk}_file_input`) as HTMLInputElement | null;
    fileInput?.click();
    setSelectedLanguage(language || '');
    setFileType(fileType);
    closeSubMenus();
  }

  const closeSubMenus = (): void => {
    handleSubMenuImportClose();
    handleTranslationsMenuClose();
  }

  const handleImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files ? event.target.files[0] : '';

    closeSubMenus();

    if (selectedFile) {
      updateIsLoading(true);
      const accessToken = await getTokenFunction();

      const formData = new FormData();
      formData.append('file', selectedFile);

      try {
        const response = await fbPostImport(
          fileType,
          `${pk}%23${sk}%23${selectedLanguage}`,
          formData,
          accessToken);
        if(response && response.status === 200){
          setStatusMessage(fbImportSuccessMessage);
          setShowSuccess(true);
        } else {
          const moreInfo = (fileType ===FileType.QUESTIONS) ? response?.data : ''
          setStatusMessage(`${fbImportFailureMessage} ${moreInfo}`);
          setShowError(true);
        }
      } catch(error){
        const moreInfo = (fileType ===FileType.QUESTIONS) ? (error as AxiosResponse)?.data : ''
        setStatusMessage(`${fbImportFailureMessage} ${moreInfo}`);
        setShowError(true);
      }
      updateIsLoading(false);
    }
  }

  const onCloseHandler = () : void => {
    closeSubMenus();
    onClose();
  }

  return (
    <>
    <Menu
      data-testid="ContextMenu"
      anchorEl={anchorEl}
      open={open}
      onClose={onCloseHandler}
      onClick={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <MenuItem
        disabled={false}
        data-testid="Edit"
        onClick={() => navigate('/edit', { state: { formData: { pk, sk }, tab: 'edit' } })}
        aria-label={makeDisabled ? 'View form' : 'Edit form'}
      >
        {makeDisabled ? 'View' : 'Edit' }
      </MenuItem>
      <MenuItem
        disabled={makeDisabled}
        data-testid="Release"
        onClick={() => handleReleaseForm(pk, sk)}
        aria-label="Release form"
      >
        Release
      </MenuItem>
      <MenuItem
        disabled={languages.length < 2 && !(isGlobalAdmin && isGlobalForm)}
        data-testid="Translations"
        onMouseEnter={fetchTranslationCompletion}
        onClick={handleTranslationsMenuClick}
        aria-label="Translations"
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
      >
        <span>Translations ({languages.length})</span>
        <span style={{ fontSize: '12px', color: '#999' }}>›</span>
      </MenuItem>
      <MenuItem
        disabled={makeDisabled}
        data-testid="Deactivate"
        onClick={() => handleActivationForm(pk, sk, !active)}
        aria-label="Deactivate form"
      >
        {active ? 'Deactivate' : 'Activate'}
      </MenuItem>
      <MenuItem
        disabled={false}
        data-testid="Duplicate"
        onClick={() => handleDuplicateForm(pk, sk)}
        aria-label="Duplicate form"
      >
        Duplicate
      </MenuItem>
      <MenuItem
        disabled={hasExtension || (isGlobalForm && isGlobalAdmin) || (!isGlobalForm && isLocalAdmin)}
        data-testid="Extend"
        onClick={() => handleExtendForm(pk, sk)}
        aria-label="Extend form"
      >
        Extend
      </MenuItem>

      <MenuItem
        disabled={makeDisabled}
        data-testid="Export"
        onClick={handleExportForm}
        aria-label="Export"
      >
        Export
      </MenuItem>

      <MenuItem
        disabled={makeDisabled}
        data-testid="Import"
        onClick={handleImportMenuClick}
        aria-label="Import"
      >
        Import
      </MenuItem>

      <Menu
        anchorEl={submenuAnchorElImport}
        open={Boolean(submenuAnchorElImport)}
        onClose={onCloseHandler}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => handleImportOption(FileType.FORM,'')} data-testid="import-option-form">
          Form
        </MenuItem>

        <MenuItem onClick={() => handleImportOption(FileType.QUESTIONS, selectedLanguage)} data-testid="import-option-questions">
          Questions
        </MenuItem>

      </Menu>

      <MenuItem
        disabled={makeDisabled}
        data-testid="Delete"
        onClick={() => {
            setTitle('Delete form');
            setMessage(
              hasExtension
                ? `${deleteWarningWithExtension}`
                : `${deleteWarning}`
            );
            setAction('Delete');
            setExecuteMethod('');
            setMethod('handleDeleteForm');
            setPk(pk);
            setSk(sk);
            setTrigger(true);
        }}
        aria-label="Delete form"
      >
        Delete
      </MenuItem>
    </Menu>
    {/* Translations submenu is rendered outside the parent Menu to prevent
        React's portal event bubbling from closing the parent when the user
        interacts with the read-only language list. */}
    <Menu
      anchorEl={submenuAnchorElTranslations}
      open={Boolean(submenuAnchorElTranslations)}
      onClose={handleTranslationsMenuClose}
      anchorOrigin={{ vertical: 'top', horizontal: submenuOpenLeft ? 'left' : 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: submenuOpenLeft ? 'right' : 'left' }}
    >
      {sortedLanguages.map((lang) => {
          const completion = translationCompletion !== null && translationCompletion !== false ? translationCompletion : null;
          const pct = completion?.[lang];
          return (
            <MenuItem
              key={lang}
              onClick={() => {
                handleTranslationsMenuClose();
                onClose();
                navigate('/edit', { state: { formData: { pk, sk }, language: lang, tab: 'translations' } });
              }}
              sx={{ minWidth: 200 }}
            >
              <Box display="flex" justifyContent="space-between" width="100%" gap={3}>
                <span>{getLanguageDisplayName(lang, frontline?.settings.languageDisplayNames || {})}</span>
                <Typography
                  variant="body2"
                  sx={{
                    color: pct === undefined ? '#aaa' : pct === 100 ? '#2e7d32' : pct > 0 ? '#ed6c02' : '#999',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {translationCompletion === null
                    ? <CircularProgress size={10} sx={{ color: '#bbb' }} />
                    : translationCompletion === false
                    ? '—'
                    : pct === undefined ? '—' : `${pct}%`
                  }
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
    </Menu>
      {/* ── Language picker dialog (global admin on global forms) ── */}
    <Dialog
      open={translationsDialogOpen}
      onClose={() => setTranslationsDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '80vh' } }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
        Select language to translate
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search languages…"
            value={translationsDialogSearch}
            onChange={(e) => setTranslationsDialogSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" sx={{ color: '#aaa' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List disablePadding>
          {sortedLanguages
            .filter((lang) => {
              const name = getLanguageDisplayName(lang, frontline?.settings.languageDisplayNames || {}).toLowerCase();
              return name.includes(translationsDialogSearch.toLowerCase());
            })
            .map((lang) => {
              const completion = translationCompletion !== null && translationCompletion !== false ? translationCompletion : null;
              const pct = completion?.[lang];
              return (
                <ListItemButton
                  key={lang}
                  onClick={() => {
                    setTranslationsDialogOpen(false);
                    onClose();
                    navigate('/edit', { state: { formData: { pk, sk }, language: lang, tab: 'translations' } });
                  }}
                  sx={{ px: 2, py: 1.2 }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography variant="body2">
                      {getLanguageDisplayName(lang, frontline?.settings.languageDisplayNames || {})}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: pct === undefined ? '#aaa' : pct === 100 ? '#2e7d32' : pct > 0 ? '#ed6c02' : '#999',
                        minWidth: 40,
                        textAlign: 'right',
                      }}
                    >
                      {translationCompletion === null
                        ? <CircularProgress size={10} sx={{ color: '#bbb' }} />
                        : translationCompletion === false
                        ? '—'
                        : pct === undefined ? '—' : `${pct}%`}
                    </Typography>
                  </Box>
                </ListItemButton>
              );
            })}
        </List>
      </DialogContent>
    </Dialog>
    <Input
      id={`${pk}_${sk}_file_input`}
      type="file"
      onChange={handleImportFileChange}
      disabled={false}
      style={{ display: 'none' }}
    />
    </>
  );
};

export default FormCardMenuComponent;
